import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, Chip, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  Checkbox, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, IconButton, Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import PaymentsIcon from "@mui/icons-material/Payments";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import api from "../services/api";
import { todayISO } from "../utils/date";

const fmt = (n) => {
  const v = Number(n) || 0;
  return (v < 0 ? "-$" : "$") + Math.abs(v).toFixed(2);
};

export default function BrokerPayments() {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);

  const fetchBrokers = useCallback(() => {
    setLoading(true);
    api.get("/brokers")
      .then((r) => setBrokers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { console.error("brokers list:", e); setBrokers([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchBrokers();
    // Accounts come from the existing subaccounts list used everywhere else
    api.get("/saleInvoices/accounts")
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        api.get("/purchaseInvoices/accounts")
          .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
          .catch(() => setAccounts([]));
      });
  }, [fetchBrokers]);

  const grand = useMemo(() => {
    return brokers.reduce((s, b) => s + (Number(b.balance) || 0), 0);
  }, [brokers]);

  return (
    <Box p={2}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Broker Payments</Typography>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={() => setAssignOpen(true)}
        >
          Assign Invoices to Broker
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Chip variant="outlined" label={`${brokers.length} broker${brokers.length === 1 ? "" : "s"}`} />
          <Chip variant="outlined" color="error" label={`Total we owe brokers: ${fmt(-grand)}`} />
        </Box>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>Loading…</Paper>
      ) : brokers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          No brokers yet. Click <strong>Assign Invoices to Broker</strong> to transfer
          unpaid Purchase Invoices onto a broker's account.
        </Paper>
      ) : brokers.map((b) => (
        <BrokerCard key={b.brokerName} broker={b} onPay={() => setPayTarget(b)} onChanged={fetchBrokers} />
      ))}

      <AssignDialog
        open={assignOpen}
        accounts={accounts}
        onClose={() => setAssignOpen(false)}
        onSaved={() => { setAssignOpen(false); fetchBrokers(); }}
      />
      <PayBrokerDialog
        broker={payTarget}
        onClose={() => setPayTarget(null)}
        onSaved={() => { setPayTarget(null); fetchBrokers(); }}
      />
    </Box>
  );
}

/* ================= broker card with assigned invoices drawer ================= */
function BrokerCard({ broker, onPay, onChanged }) {
  const owe = -(Number(broker.balance) || 0);  // positive if we owe him

  const removeAssignment = async (id) => {
    if (!window.confirm("Remove this assignment? The invoice goes back to its original supplier as unpaid.")) return;
    try {
      await api.delete(`/brokers/assign/${id}`);
      onChanged && onChanged();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1, flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{broker.brokerName}</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Chip
              size="small"
              label={`Owed: ${fmt(owe)}`}
              color={Math.abs(owe) < 0.005 ? "success" : "error"}
              sx={{ fontWeight: 700 }}
            />
            <Button
              size="small" variant="contained" color="success"
              startIcon={<PaymentsIcon />}
              onClick={(e) => { e.stopPropagation(); onPay(); }}
              disabled={owe < 0.005}
            >
              Pay
            </Button>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {broker.assignments.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
            No assigned invoices.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Source supplier</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {broker.assignments.map((a) => (
                  <TableRow key={a.Assignment_ID}>
                    <TableCell>{a.Invoice_ID}</TableCell>
                    <TableCell>{a.SourceAccountName}</TableCell>
                    <TableCell>{a.AssignedDate ? String(a.AssignedDate).slice(0, 10) : "-"}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(a.Amount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove assignment">
                        <IconButton size="small" color="warning" onClick={() => removeAssignment(a.Assignment_ID)}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/* ================= assign invoices to broker dialog ================= */
function AssignDialog({ open, accounts, onClose, onSaved }) {
  const today = todayISO();
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [broker, setBroker] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPending = useCallback(() => {
    if (!open) return;
    setLoading(true);
    api.get("/brokers/pending", { params: { name: search, fromDate, toDate } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { console.error("brokers/pending:", e); setRows([]); })
      .finally(() => setLoading(false));
  }, [open, search, fromDate, toDate]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  useEffect(() => {
    if (open) { setBroker(null); setSelected(new Set()); }
  }, [open]);

  const totalSelected = useMemo(() => {
    let t = 0;
    for (const r of rows) if (selected.has(r.Master_ID)) t += Number(r.Balance) || 0;
    return t;
  }, [rows, selected]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.Master_ID));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.Master_ID)));
  };

  const save = async () => {
    if (!broker?.SubAccount_Name) { alert("Pick a broker account."); return; }
    if (selected.size === 0)      { alert("Pick at least one invoice."); return; }
    try {
      setSaving(true);
      await api.post("/brokers/assign", {
        brokerName: broker.SubAccount_Name,
        invoiceIds: Array.from(selected),
      });
      onSaved && onSaved();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Invoices to Broker</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(o) => o?.SubAccount_Name || ""}
              value={broker}
              onChange={(_, v) => setBroker(v)}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              renderInput={(p) => (
                <TextField {...p} label="Broker / Agent" required size="small" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth size="small"
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth size="small"
              value={toDate} onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth size="small" label="Filter by supplier name"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>

        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allChecked}
                    indeterminate={!allChecked && selected.size > 0}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>No unassigned unpaid invoices in this range.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.Master_ID} hover onClick={() => toggle(r.Master_ID)} sx={{ cursor: "pointer" }}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(r.Master_ID)} onChange={() => toggle(r.Master_ID)} />
                  </TableCell>
                  <TableCell>{r.Master_ID}</TableCell>
                  <TableCell>{r.Name}</TableCell>
                  <TableCell>{r.Date ? String(r.Date).slice(0, 10) : "-"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.Balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Chip variant="outlined" label={`${selected.size} invoice${selected.size === 1 ? "" : "s"} selected`} />
          <Typography sx={{ fontWeight: 700 }}>Total transferring: {fmt(totalSelected)}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || selected.size === 0 || !broker} onClick={save}>
          {saving ? "Assigning…" : `Assign ${selected.size} to ${broker?.SubAccount_Name || "broker"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================= pay broker dialog ================= */
function PayBrokerDialog({ broker, onClose, onSaved }) {
  const today = todayISO();
  const owe = broker ? -(Number(broker.balance) || 0) : 0;

  const [method, setMethod] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [paymentDate, setPaymentDate] = useState(today);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (broker) {
      setMethod("Cash");
      setAmount(owe.toFixed(2));
      setCheckNumber("");
      setPaymentDate(today);
    }
  }, [broker, owe, today]);

  if (!broker) return null;

  const amt = Number(amount) || 0;

  const save = async () => {
    if (amt <= 0) { alert("Amount must be > 0"); return; }
    if (method === "Check" && !checkNumber.trim()) { alert("Check # required"); return; }
    try {
      setSaving(true);
      await api.post("/brokers/pay", {
        brokerName:  broker.brokerName,
        amount:      amt,
        method,
        checkNumber: method === "Check" ? checkNumber.trim() : null,
        paymentDate,
      });
      onSaved && onSaved();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!broker} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pay Broker — {broker.brokerName}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Currently owed</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{fmt(owe)}</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Method"
              value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
            </TextField>
          </Grid>
          {method === "Check" && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Check #"
                value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              type="number" fullWidth label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: "0.01", min: "0" }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText={
                amt < owe - 0.005 ? "Partial — broker stays Owed"
                  : amt > owe + 0.005 ? "Overpayment — broker becomes Receivable"
                  : "Will clear broker balance"
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              type="date" fullWidth label="Date"
              InputLabelProps={{ shrink: true }}
              value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Chip
            variant="outlined" color="primary"
            label={`Will post $${amt.toFixed(2)} ${method === "Check" ? "check" : "cash"} to Daily Cash for ${broker.brokerName} on ${paymentDate}`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Pay Broker"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
